<template>
    <div v-if="loaded">
        <h2 class="site-title">{{ $t('nav.materials') }}</h2>
        <v-tabs v-model="activeTab">
            <v-tab v-for="table in tables" :key="table.type">{{ table.label }}</v-tab>
            <v-tab>{{ additional.label }}</v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab">
            <v-tab-item v-for="table in tables" :key="table.type">
                <div class="calculable-items-container">
                    <v-subheader>
                        <span class="mr-5">{{ table.label }}</span>
                        <material-form :type="table.type" :label="table.label">
                            <template v-slot:activator="{ dialog }">
                                <v-btn
                                    v-on="dialog"
                                    class="mr-2" color="primary" dark
                                    small>
                                    <v-icon color="white">
                                        mdi-plus
                                    </v-icon>
                                    <span>{{ $t('base.add') }}</span>
                                </v-btn>
                            </template>
                        </material-form>
                    </v-subheader>
                    <v-data-table
                        :headers="baseHeaders"
                        :items="table.items"
                        class="elevation-1"
                        disable-pagination
                        hide-default-footer>
                        <template v-slot:item.unit="{ item }">
                            {{ unitLabel(item.unit) }}
                        </template>
                        <template v-slot:item.tax="{ item }">
                            {{ item.tax | trailingZeros }}
                        </template>
                        <template v-slot:item.action="{ item }">
                            <material-form :item="item" :type="table.type"></material-form>
                            <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    @click="deleteItem(item)"
                                    color="orange"
                                    :disabled="loading"
                                    fab small
                                    v-on="on">
                                    <v-icon color="white">
                                        mdi-delete
                                    </v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('base.remove') }}</span>
                        </v-tooltip>
                        </template>
                    </v-data-table>
                </div>
            </v-tab-item>
            <v-tab-item>
                <div class="calculable-items-container">
                    <v-subheader>
                        <span class="mr-5">{{ additional.label }}</span>
                        <material-form :type="additional.type" :label="additional.label">
                            <template v-slot:activator="{ dialog }">
                                <v-btn
                                    v-on="dialog"
                                    class="mr-2" color="primary" dark
                                    small>
                                    <v-icon color="white">
                                        mdi-plus
                                    </v-icon>
                                    <span>{{ $t('base.add') }}</span>
                                </v-btn>
                            </template>
                        </material-form>
                    </v-subheader>
                    <v-data-table
                        :headers="additionalHeaders"
                        :items="additional.items"
                        class="elevation-1"
                        disable-pagination
                        hide-default-footer>
                        <template v-slot:item.unit="{ item }">
                            {{ unitLabel(item.unit) }}
                        </template>
                        <template v-slot:item.tax="{ item }">
                            {{ item.tax | trailingZeros }}
                        </template>
                        <template v-slot:item.surfaces="{ item }">
                            <v-btn
                                v-for="(surface, index) in item.surfaces"
                                :key="index"
                                :color="['indigo darken-3', 'teal darken-3', 'brown darken-4'][index]" block outlined
                                x-small>{{ surface.name[$locale] }}
                            </v-btn>
                        </template>
                        <template v-slot:item.action="{ item }">
                            <material-form :item="item" :type="additional.type">
                                <template v-slot:activator="{ dialog }">
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on: tooltip }">
                                            <v-btn
                                                v-on="{...tooltip, ...dialog}"
                                                class="mr-2" color="blue" dark
                                                fab
                                                small>
                                                <v-icon color="white">
                                                    mdi-square-edit-outline
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ $t('base.edit') + ' ' + item.description[$locale] }}</span>
                                    </v-tooltip>
                                </template>
                            </material-form>
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        @click="deleteItem(item)"
                                        color="orange"
                                        :disabled="loading"
                                        fab small
                                        v-on="on">
                                        <v-icon color="white">
                                            mdi-delete
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $t('base.remove') }}</span>
                            </v-tooltip>
                        </template>
                    </v-data-table>
                </div>
            </v-tab-item>
        </v-tabs-items>
    </div>
</template>

<script>
import type from '@/helpers/ItemType'
import units from '@/helpers/units'
import MaterialForm from './materials/MaterialForm'

export default {
    components: {
        MaterialForm
    },
    computed: {
        tables() {
            return [this.planks, this.elevation, this.stair, this.joists, this.slats, this.corner]
        },
        planks() {
            return {
                type: type.plank.id,
                label: type.plank.label,
                items: this.$store.getters.items(type.plank.id)
            }
        },
        joists() {
            return {
                type: type.joist.id,
                label: type.joist.label,
                items: this.$store.getters.items(type.joist.id)
            }
        },
        slats() {
            return {
                type: type.slat.id,
                label: type.slat.label,
                items: this.$store.getters.items(type.slat.id)
            }
        },
        additional() {
            return {
                type: 'additional',
                label: type.additional.label,
                items: this.$store.getters.items(type.additional.id)
            }
        },
        elevation() {
            return {
                type: 'elevation',
                label: type.elevation.label,
                items: this.$store.getters.items(type.elevation.id)
            }
        },
        stair() {
            return {
                type: 'stair',
                label: type.stair.label,
                items: this.$store.getters.items(type.stair.id)
            }
        },
        corner() {
            return {
                type: 'corner',
                label: type.corner.label,
                items: this.$store.getters.items(type.corner.id)
            }
        },
        commonHeaders() {
            return [
                {
                    text: this.$t('materials.description'),
                    value: 'description.' + this.$locale
                },
                {
                    text: this.$t('materials.firmao_product_id'),
                    value: 'firmao_product_id'
                },
                {
                    text: this.$t('materials.price'),
                    value: 'price'
                },
                {
                    text: this.$t('materials.tax'),
                    value: 'tax'
                },
                {
                    text: this.$t('materials.unit'),
                    value: 'unit'
                }
            ]
        },
        baseHeaders() {
            return this.commonHeaders.concat([
                {
                    text: this.$t('base.actions'),
                    value: 'action',
                    sortable: false,
                    align: 'center'
                }
            ])
        },
        additionalHeaders() {
            return this.commonHeaders.concat([
                {
                    text: this.$t('materials.package'),
                    value: 'package'
                },
                {
                    text: this.$t('materials.amount_for_unit'),
                    value: 'amount_for_unit'
                },
                {
                    text: this.$t('materials.surfaces.used'),
                    value: 'surfaces',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: this.$t('base.actions'),
                    value: 'action',
                    sortable: false,
                    width: 150,
                    align: 'center'
                }
            ])
        }
    },
    name: "Materials",
    data() {
        return {
            items: [],
            loading: false,
            loaded: false,
            activeTab: null,
        }
    },
    methods: {
        unitLabel(id) {
            let unit = units.find(unit => unit.id == id)
            return unit ? unit.label : ""
        },
        deleteItem(item) {
            if (confirm(this.$t('materials.delete-item', {product: item.description[this.$locale]}))) {
                this.loading = true
                this.$http.delete('items/' + item.id)
                    .then(() => {
                        this.$store.commit('removeItem', item.id)
                    })
                    .finally(() => {
                        this.loading = false
                    })
            }
        }
    },
    mounted() {
        this.loading = true
        this.$store.dispatch('prepareMaterials')
            .then(() => {
                this.loading = false
                this.loaded = true
            })
            .catch(() => {
                this.loading = false
            })
    }
}
</script>

<style scoped>
.calculable-items-container {
    margin-bottom: 20px;
}
</style>
